import { useState, useCallback } from 'react';
import {
    GeographyOutcomeKind,
    GeographyStep,
    SiteImportOutcomeKind,
    type CreatedSite,
    type ExistingDraftSite,
    type GeographyFailure,
    type GeographyOutcome,
    type ImportResult,
    type ImportRowResult,
    type Project,
    type ProjectGeometrySite,
    type SiteImportOutcome,
    type UseImportSitesOptions,
} from './useImportSites.types';
import { isFetchError, isConflictError, type PolygonFeature } from '../state/api/organisationEngineSlice';

const readErrorMessage = (value: unknown): string | undefined => {
    if (typeof value !== 'object' || value === null) return undefined;
    if ('error' in value && typeof value.error === 'string') return value.error;
    if ('message' in value && typeof value.message === 'string') return value.message;
    return undefined;
};

const describeFailure = (step: GeographyStep, err: unknown): GeographyFailure => {
    if (isFetchError(err)) {
        return {
            step,
            status: typeof err.status === 'number' ? err.status : undefined,
            message: readErrorMessage(err.data),
        };
    }
    if (err instanceof Error) return { step, message: err.message };
    return { step };
};

type ImportProgress = { current: number; total: number };

type SiteCreation =
    | { kind: SiteImportOutcomeKind.Imported; site: CreatedSite }
    | { kind: SiteImportOutcomeKind.Skipped }
    | { kind: SiteImportOutcomeKind.Failed };

const toPolygonFeatures = (sites: ProjectGeometrySite[]): PolygonFeature[] =>
    sites
        .filter(site => (site.geometry?.coordinates?.length ?? 0) > 0)
        .map(site => ({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: site.geometry!.coordinates! },
            properties: {},
        }));

const geographyFailedFor = (row: ImportRowResult): boolean =>
    row.outcome.kind === SiteImportOutcomeKind.Imported && row.outcome.geography.kind === GeographyOutcomeKind.Failed;

const countByKind = (rows: ImportRowResult[]) => ({
    imported: rows.filter(r => r.outcome.kind === SiteImportOutcomeKind.Imported && !geographyFailedFor(r)).length,
    geographyFailed: rows.filter(geographyFailedFor).length,
    skipped: rows.filter(r => r.outcome.kind === SiteImportOutcomeKind.Skipped).length,
    failed: rows.filter(r => r.outcome.kind === SiteImportOutcomeKind.Failed).length,
});

/** Create one Site in the engine; classify the outcome.
 *  Exported for direct unit testing — the hook composes it inside its loop. */
export const tryCreateSite = async (
    createSite: UseImportSitesOptions['createSite'],
    bcId: string,
    project: Project
): Promise<SiteCreation> => {
    try {
        const site = await createSite({
            bcId,
            body: { name: project.projectName, business_central_id: project.projectId },
        });
        return { kind: SiteImportOutcomeKind.Imported, site };
    } catch (err) {
        return isFetchError(err) && isConflictError(err) ? { kind: SiteImportOutcomeKind.Skipped } : { kind: SiteImportOutcomeKind.Failed };
    }
};

/** For an already-created Site, fetch its polygons from the Go backend,
 *  normalize via the engine, and POST the resulting Geography. Any failure
 *  along the chain collapses to `kind: Failed` — the Site itself is never
 *  affected. Exported for direct unit testing. */
export const tryAttachGeography = async (
    deps: Pick<UseImportSitesOptions, 'fetchProjectSites' | 'convertGeoJSONToGeography' | 'createGeography'>,
    bcId: string,
    projectId: string,
    siteId: string
): Promise<GeographyOutcome> => {
    let projectSites: ProjectGeometrySite[];
    try {
        projectSites = await deps.fetchProjectSites(projectId);
    } catch (err) {
        return { kind: GeographyOutcomeKind.Failed, failure: describeFailure(GeographyStep.Fetch, err) };
    }

    const features = toPolygonFeatures(projectSites);
    if (features.length === 0) return { kind: GeographyOutcomeKind.NoGeographyAvailable };

    let canonical: Awaited<ReturnType<UseImportSitesOptions['convertGeoJSONToGeography']>>;
    try {
        canonical = await deps.convertGeoJSONToGeography({ type: 'FeatureCollection', features });
    } catch (err) {
        return { kind: GeographyOutcomeKind.Failed, failure: describeFailure(GeographyStep.Convert, err) };
    }

    try {
        await deps.createGeography({ bcId, siteId, body: canonical });
    } catch (err) {
        return { kind: GeographyOutcomeKind.Failed, failure: describeFailure(GeographyStep.Create, err) };
    }

    return { kind: GeographyOutcomeKind.Created };
};

/** Full per-project pipeline. When `existing` is supplied the caller has
 *  already matched this project to an existing draft site by BC id: if
 *  that site already has geography, there's nothing to do (Skipped);
 *  otherwise the recovery branch attempts to attach geography without
 *  re-creating the site. When `existing` is undefined we take the normal
 *  create-then-attach path; a 409 on POST /sites still resolves cleanly
 *  as Skipped for the (defensive) case of an active/archived conflict
 *  the draft-only preload didn't cover. Exported for direct unit testing. */
export const importOneProject = async (
    deps: UseImportSitesOptions,
    bcId: string,
    project: Project,
    existing?: ExistingDraftSite
): Promise<SiteImportOutcome> => {
    if (existing?.hasGeography) return { kind: SiteImportOutcomeKind.Skipped };

    if (existing) {
        // Draft site already exists but has no geography — attach directly
        // to it. Whatever the geography chain reports (Created, Failed,
        // NoGeographyAvailable) rolls up into an Imported row so the
        // aggregate `geographyFailed` derivation still catches recovery
        // failures.
        const geography = await tryAttachGeography(deps, bcId, project.projectId, existing.siteId);
        return { kind: SiteImportOutcomeKind.Imported, geography };
    }

    const creation = await tryCreateSite(deps.createSite, bcId, project);
    if (creation.kind !== SiteImportOutcomeKind.Imported) return creation;
    const geography = await tryAttachGeography(deps, bcId, project.projectId, creation.site.id);
    return { kind: SiteImportOutcomeKind.Imported, geography };
};

const useImportSites = ({
    createSite,
    fetchProjectSites,
    convertGeoJSONToGeography,
    createGeography,
    fetchExistingDraftSites,
}: UseImportSitesOptions) => {
    const [isImporting, setIsImporting] = useState(false);
    const [progress, setProgress] = useState<ImportProgress | null>(null);
    const [result, setResult] = useState<ImportResult | null>(null);

    const importSites = useCallback(
        async (bcId: string, projects: Project[]) => {
            setIsImporting(true);
            setProgress({ current: 0, total: projects.length });
            setResult(null);

            // Preload is a best-effort optimisation: on failure we still run
            // the full create-then-attach path per project. A 409 on POST
            // /sites still resolves cleanly as Skipped, so losing the
            // recovery-attach shortcut only downgrades behaviour to the
            // pre-preload state — the wizard must not lock up on a
            // preload-side error (e.g. schema drift, transient network).
            let existingSites: Awaited<ReturnType<typeof fetchExistingDraftSites>> = [];
            try {
                existingSites = await fetchExistingDraftSites(bcId);
            } catch (err) {
                console.warn('Import preload failed; continuing without recovery-attach optimisation.', err);
            }
            const existingByBcId = new Map(existingSites.map(s => [s.businessCentralId, s]));

            const rows: ImportRowResult[] = [];
            for (const [i, project] of projects.entries()) {
                const outcome = await importOneProject(
                    { createSite, fetchProjectSites, convertGeoJSONToGeography, createGeography, fetchExistingDraftSites },
                    bcId,
                    project,
                    existingByBcId.get(project.projectId)
                );
                rows.push({ projectId: project.projectId, projectName: project.projectName, outcome });
                setProgress({ current: i + 1, total: projects.length });
            }

            setIsImporting(false);
            setResult({ ...countByKind(rows), rows });
        },
        [createSite, fetchProjectSites, convertGeoJSONToGeography, createGeography, fetchExistingDraftSites]
    );

    return { importSites, isImporting, progress, result };
};

export default useImportSites;
